<template>
    <div>
        <div class="header">Informations de l'entreprise</div>
        <v-form v-model="valid" ref="form">
            <v-row dense>
                <v-col cols="4">
                    <v-text-field-filled
                        label="Siret"
                        type="number"
                        v-model="company.siret"
                        @input="onInputSetTruncatedSiret(company.siret)"
                        @keydown="isSiretValided = false"
                        :rules="siretRules"
                        :error="isSiretError"
                        :error-messages="siretErrorMessage"
                        :hide-details="!isSiretError"
                    />
                </v-col>
                <v-col cols="4">
                    <v-text-field-filled
                        label="Siren"
                        type="number"
                        v-model="company.siren"
                        :readonly="isSiretValided"
                        :error="isSirenError"
                        :error-messages="sirenErrorMessage"
                        :hide-details="!isSirenError"
                    />
                </v-col>
                <v-col cols="4">
                    <v-text-field-filled label="Numéro de TVA" v-model="company.vatNumber" />
                </v-col>
                <v-col cols="3" />
            </v-row>
            <v-row dense>
                <v-col cols="4">
                    <v-text-field-filled
                        label="Nom"
                        v-model="company.name"
                        :readonly="isSiretValided"
                    />
                </v-col>
                <v-col cols="4">
                    <v-text-field-filled label="Nom légal" v-model="company.legalUnitName" />
                </v-col>
                <v-col cols="4">
                    <v-text-field-filled label="Nom commercial" v-model="company.tradeName" />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="8">
                    <v-text-field-filled label="Adresse" v-model="company.address" />
                </v-col>
                <v-col cols="4">
                    <v-text-field-filled label="Code Postal" v-model="company.postalCode" />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="6">
                    <v-text-field-filled label="Ville" v-model="company.city" />
                </v-col>
                <v-col cols="6">
                    <v-text-field-filled label="Pays" v-model="company.country" />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="6">
                    <v-autocomplete-filled
                        v-model="company.legalForm"
                        :items="legalForms"
                        item-value="id"
                        :rules:="legalFormsRules"
                        :error="isLegalFormError"
                        :error-messages="legalFormErrorMessage"
                        :disabled="isAutomaticLegalForm"
                        :hide-details="!isLegalFormError"
                        :item-title="formatItemTitle"
                        label="Forme juridique"
                    />
                </v-col>
                <v-col cols="6">
                    <v-select-filled
                        multiple
                        chips
                        v-model="company.salesAccounts"
                        :items="availableSalesAccounts"
                        item-value="name"
                        hide-details="auto"
                        :label="$t('settings.salesAccountingAccount')"
                        density="compact"
                        :item-title="(item) => item.description"
                    >
                    </v-select-filled>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-combobox-filled
                        chips
                        v-model="company.activities"
                        :items="allActivities"
                        item-value="name"
                        multiple
                        item-title="name"
                        label="Code NAF"
                        :disabled="isAutomaticActivities"
                    >
                    </v-combobox-filled>
                </v-col>
            </v-row>
        </v-form>
    </div>
</template>

<script lang="ts">
import { companyInfo } from "@/types/registerServices.types";
import { Apollo } from "@/decorators";

import { truncateInput } from "@/helpers/formInput.helper";
import COMPANY_FROM_SIRET from "@/graphql/company/CompanyFromSiret.gql";
import COMPANY_ACTIVITIES from "@/graphql/register/CompanyActivity.gql";
import { Component, Vue, toNative, Prop, Watch, Model, Emit } from "vue-facing-decorator";

@Component
export class CompanyForm extends Vue {
    @Model({ default: null, name: "data" }) readonly company: companyInfo;
    @Prop({ default: true }) isTvaPayed: boolean;

    isSiretValided: boolean = false;
    isAutomaticLegalForm: boolean = false;
    isAutomaticActivities: boolean = false;
    valid: boolean = true;

    legalForms: any = [
        { id: "NONE", name: "" },
        { id: "ASSO", name: "Association" },
        { id: "CE", name: "Comité d'entreprise" },
        { id: "CL", name: "Collectivité locale" },
        { id: "CNE", name: "Commune" },
        { id: "COOP", name: "Coopérative" },
        { id: "CUMA", name: "Coopérative d'utilisation de matériel agricole" },
        { id: "DPT", name: "Département" },
        { id: "EARL", name: "Exploitation agricole à responsabilité limitée" },
        { id: "EC", name: "Entreprise collective" },
        { id: "EI", name: "Entreprise individuelle" },
        { id: "EIRL", name: "Entreprise individuelle à responsabilité limitée" },
        { id: "ETRANGER", name: "Personne morale de droit étranger" },
        { id: "EURL", name: "Entreprise unipersonnelle à responsabilité limitée" },
        { id: "FEDE", name: "Fédération" },
        { id: "FOND", name: "Fondation" },
        { id: "GAEC", name: "Groupement agricole d'exploitation en commun" },
        { id: "GEIE", name: "Groupement européen d'intérêt économique" },
        { id: "GF", name: "Groupement forestier" },
        { id: "GFA", name: "Groupement foncier agricole" },
        { id: "GFR", name: "Groupement foncier rural" },
        { id: "GIE", name: "Groupement d'intérêt économique" },
        { id: "GPT", name: "Groupement" },
        { id: "GREFFE", name: "Greffe" },
        { id: "IND", name: "Indivision" },
        { id: "MUT", name: "Mutuelle" },
        { id: "SA", name: "Société anonyme" },
        { id: "SAE", name: "Société anonyme d'exploitation" },
        { id: "SARL", name: "Société à responsabilité limitée" },
        { id: "SELARL", name: "Société d'exercice libéral à responsabilité limitée" },
        { id: "SAS", name: "Société anonyme par actions simplifiée" },
        { id: "SASU", name: "SASU" },
        { id: "SC", name: "Société civile (autre que infra)" },
        { id: "SCA", name: "Société en commandite par actions" },
        { id: "SCCV", name: "Société civile de construction vente" },
        { id: "SCE", name: "Société civile d'exploitation" },
        { id: "SCEA", name: "Société civile d'exploitation agricole" },
        { id: "SCEV", name: "Société civile d'exploitation vinicole" },
        { id: "SCH", name: "Société civile holding" },
        { id: "SCI", name: "Société civile immobilière" },
        { id: "SCM", name: "Société civile de moyens" },
        { id: "SCP", name: "Société civile professionnelle" },
        { id: "SCPG", name: "Société civile participation gestion" },
        { id: "SCPI", name: "Société civile de placement collectif immobilier" },
        { id: "SCS", name: "Société en commandite simple" },
        { id: "SCVV", name: "Société civile de vente volontaire" },
        { id: "SDE", name: "Société de droit étranger" },
        { id: "SNC", name: "Société en nom collectif" },
    ];

    availableSalesAccounts = [
        { name: "701", description: "Ventes de produits finis" },
        { name: "702", description: "Ventes de produits intermédiaires" },
        { name: "704", description: "Travaux" },
        { name: "705", description: "Études" },
        { name: "706", description: "Prestations de services" },
        { name: "707", description: "Ventes de marchandises" },
        { name: "708", description: "Produits des activités annexes" },
        {
            name: "7081",
            description: "Produits des services exploités dans l'intérêt du personnel",
        },
        { name: "7082", description: "Commissions et courtages" },
        { name: "7083", description: "Locations diverses" },
        { name: "7084", description: "Mise à disposition de personnel facturée" },
        { name: "7085", description: "Frais de port et livraison" },
        { name: "7086", description: "Bonis sur reprises d'emballage consignés" },
        { name: "7087", description: "Bonifications obtenues des clients et primes sur ventes" },
        { name: "7088", description: "Autres produits d'activités annexes" },
        { name: "740000", description: "Subventions" },
    ];

    @Apollo({
        query: COMPANY_ACTIVITIES,
        variables() {
            return {
                variable: "",
            };
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data && data.companyActivities) {
                    this.allActivities = data.companyActivities;
                    console.log(this.allActivities);
                }
            }
        },
    })
    allActivities: Array<any> = [];

    @Watch("valid")
    update() {
        this.companyFormValidation();
    }

    @Emit
    companyFormValidation() {
        return this.valid;
    }

    @Watch("company.legalForm")
    refreshLegalFormError() {
        if (this.company?.legalForm && this.company.legalForm.length) {
            this.company.error.legalForm = "";
        }
    }

    @Watch("company.siret")
    async isSiretValid() {
        if (this.company.siret.length != 14) {
            this.company.error.siret = "Un numéro de siret doit comporter 14 chiffres";
            this.isSiretValided = false;
            return;
        }
        const result = await this.$apollo.query({
            query: COMPANY_FROM_SIRET,
            variables: {
                siret: this.company.siret,
            },
        });
        if (
            result.data.companyFromSiret.status === "CompanyNotInDB" ||
            result.data.companyFromSiret.status === "CompanyInDB"
        ) {
            this.isSiretValided = true;
            this.company.siren = result.data.companyFromSiret.company.siren;
            this.company.vatNumber = result.data.companyFromSiret.company.vatNumber;
            this.company.name = result.data.companyFromSiret.company.name;
            this.company.legalUnitName = result.data.companyFromSiret.company.legalUnitName;
            this.company.tradeName = result.data.companyFromSiret.company.tradeName;
            this.company.address = result.data.companyFromSiret.company.address;
            this.company.city = result.data.companyFromSiret.company.city;
            this.company.postalCode = result.data.companyFromSiret.company.postalCode;
            this.company.country = result.data.companyFromSiret.company.country;
            if (result.data.companyFromSiret.company.legalForm) {
                this.company.legalForm = result.data.companyFromSiret.company.legalForm;
                this.isAutomaticLegalForm = true;
            } else {
                this.isAutomaticLegalForm = false;
            }
            if (result.data.companyFromSiret.company.activities) {
                this.company.activities = result.data.companyFromSiret.company.activities;
                this.isAutomaticActivities = true;
            } else {
                this.isAutomaticActivities = false;
            }
            this.company.error.siret = "";
            this.company.error.siren = "";
            this.company.error.vatNumber = "";
        } else {
            this.company.error.siret = "Ce numéro de siret n'éxiste pas";
            this.isSiretValided = false;
        }

        // else if (result.data.companyFromSiret.status === "CompanyInDB") {
        // this.company.error.siret = "Ce numéro de siret est déja utilisé"
        // this.isSiretValided = false;
        // }
    }

    checkValidity() {
        let isValid = true;
        if (!this.company.legalForm.length || !this.isSiretValided) {
            isValid = false;
        }
        this.valid = isValid;
    }

    onInputSetTruncatedSiret(siret: string) {
        this.company.siret = truncateInput(siret, 14);
    }

    get siretRules() {
        return [(v) => !!v || "Le siret est manquant"];
    }

    get legalFormsRules() {
        return [(v) => !!v || "La forme légal est manquante"];
    }

    get salesAccountRules() {
        return [(v) => !!v.length || "Un minimum de un compte comptable de vente est requis"];
    }

    get isSiretError() {
        if (this.company.error?.siret?.length) {
            return true;
        }
        return false;
    }

    get isSirenError() {
        if (this.company.error?.siren?.length) {
            return true;
        }
        return false;
    }

    get isVATError() {
        if (this.company.error?.vatNumber?.length && this.isTvaPayed) {
            return true;
        }
        return false;
    }

    get isLegalFormError() {
        if (this.company.error?.legalForm?.length) {
            return true;
        }
        return false;
    }

    get siretErrorMessage() {
        return this.company.error?.siret || "";
    }

    get sirenErrorMessage() {
        return this.company.error?.siren || "";
    }

    get VATErrorMessage() {
        return this.company.error?.vatNumber || "";
    }

    get legalFormErrorMessage() {
        return this.company.error?.legalForm || "";
    }

    formatItemTitle(item: { id?: string; name?: string }) {
        if (!item || item.id === "NONE" || (!item.id && !item.name)) {
            return "";
        }
        return `${item.id} - ${item.name}`;
    }
}

export default toNative(CompanyForm);
</script>

<style scoped>
.header {
    text-align: center;
    margin-bottom: 2vh;
    font-weight: bold;
}
</style>
